import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    ShoppingCart, 
    Trash2, 
    ArrowRight, 
    Clock, 
    RefreshCw,
    Package,
    AlertTriangle,
    Smartphone,
    CheckCircle2,
    XCircle,
    Search,
    Gift,
    Tag,
    FileSpreadsheet,
    Download,
    CheckCircle,
    Unlock,
    Lock,
    Battery,
    Wrench,
    Shield,
    Globe
} from 'lucide-react';

const CartPage = () => {
    const navigate = useNavigate();
    const [verifying, setVerifying] = useState(false);
    const [exporting, setExporting] = useState(false);
    const { 
        cart, 
        loading, 
        removeFromCart, 
        extendReservation, 
        formattedTime, 
        hasItems,
        isExpiringSoon,
        fetchCart,
        verifyAvailability
    } = useCart();

    // Refresh cart on page load to check for ERP status changes
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const handleRemove = async (productId) => {
        try {
            await removeFromCart(productId);
            toast.success('Produit retiré du panier');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleExtend = async () => {
        try {
            await extendReservation();
            toast.success('Réservation prolongée de 10 minutes');
        } catch (error) {
            toast.error('Erreur lors de la prolongation');
        }
    };

    const handleVerifyAvailability = async () => {
        setVerifying(true);
        try {
            const result = await verifyAvailability();
            if (result.reserved_count > 0 && result.unavailable_count === 0) {
                toast.success(`${result.reserved_count} produit(s) réservé(s) avec succès !`);
            } else if (result.reserved_count > 0 && result.unavailable_count > 0) {
                toast.warning(`${result.reserved_count} réservé(s), ${result.unavailable_count} indisponible(s)`);
            } else if (result.unavailable_count > 0) {
                toast.error(`${result.unavailable_count} produit(s) plus disponible(s)`);
            }
            await fetchCart();
        } catch (error) {
            toast.error('Erreur lors de la vérification');
        } finally {
            setVerifying(false);
        }
    };

    // Export cart to Excel
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            
            const response = await fetch(`${API_URL}/api/cart/export`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors de l\'export');
            }
            
            // Get filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'panier.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            
            // Download file
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Export Excel téléchargé !');
        } catch (error) {
            toast.error('Erreur lors de l\'export du panier');
        } finally {
            setExporting(false);
        }
    };

    // Check if cart has any items needing verification
    const hasExpiredItems = cart.has_expired_items || cart.items.some(
        item => item.availability_status && item.availability_status !== 'reserved'
    );

    // Count reserved vs expired items
    const reservedItems = cart.items.filter(item => item.availability_status === 'reserved');
    const expiredOrUnavailableItems = cart.items.filter(
        item => item.availability_status && item.availability_status !== 'reserved'
    );

    const getGradeColor = (grade) => {
        switch (grade) {
            case 'A': case 'A+': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
            case 'A-': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'B+': return 'bg-blue-100 text-blue-800 border-blue-300';
            case 'B': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'B-': return 'bg-amber-100 text-amber-800 border-amber-300';
            case 'C': return 'bg-amber-100 text-amber-700 border-amber-300';
            case 'D': return 'bg-rose-100 text-rose-800 border-rose-300';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    const totalHT = reservedItems.reduce((sum, item) => sum + (item.price || 0), 0);
    
    // Negotiated discount info
    const hasNegotiatedDiscount = cart.has_negotiated_discount;
    const discountItems = cart.discount_items || {};
    const globalDiscount = cart.global_discount || 0;
    const totalDiscount = cart.total_discount || 0;
    const reservationUnlimited = cart.reservation_unlimited;
    
    // Calculate final total after negotiated discounts
    const totalAfterDiscount = totalHT - totalDiscount;

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50">
                
                <div className="container mx-auto px-6 lg:px-8 py-8">
                    <div className="flex items-center justify-between mb-8">
                        <h1 
                            className="text-3xl font-bold text-gray-900 flex items-center gap-3"
                            style={{ fontFamily: 'var(--font-heading)' }}
                        >
                            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-white" />
                            </div>
                            Mon Panier
                        </h1>
                        
                        {hasItems && (
                            <Button
                                variant="outline"
                                onClick={handleExportExcel}
                                disabled={exporting}
                                className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                                data-testid="export-cart-excel"
                            >
                                {exporting ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <FileSpreadsheet className="w-4 h-4" />
                                )}
                                Exporter Excel
                            </Button>
                        )}
                    </div>

                    {!hasItems ? (
                        <div className="text-center py-20">
                            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <ShoppingCart className="w-12 h-12 text-blue-400" />
                            </div>
                            <h2 
                                className="text-2xl font-bold text-gray-900 mb-3"
                                style={{ fontFamily: 'var(--font-heading)' }}
                            >
                                Votre panier est vide
                            </h2>
                            <p className="text-gray-500 mb-8 text-lg">Ajoutez des produits depuis le catalogue</p>
                            <Button 
                                onClick={() => navigate('/catalog')} 
                                className="rounded-lg bg-blue-600 text-white font-bold px-8 py-6 text-lg hover:bg-blue-700 transition-colors"
                                data-testid="go-to-catalog"
                            >
                                Parcourir le catalogue
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                    ) : (
                        <div className="grid lg:grid-cols-3 gap-8">
                            {/* Cart Items */}
                            <div className="lg:col-span-2 space-y-4">
                            {/* Expired Items Alert + Verify Button */}
                            {hasExpiredItems && (
                                <div className="rounded-xl p-5 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-100">
                                                <AlertTriangle className="w-6 h-6 text-red-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-red-800">
                                                    {expiredOrUnavailableItems.length} produit(s) nécessite(nt) vérification
                                                </p>
                                                <p className="text-sm text-red-600">
                                                    La réservation a expiré ou le produit n'est plus disponible
                                                </p>
                                            </div>
                                        </div>
                                        <Button 
                                            onClick={handleVerifyAvailability}
                                            disabled={verifying}
                                            className="rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700"
                                            data-testid="verify-availability-btn"
                                        >
                                            {verifying ? (
                                                <>
                                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                    Vérification...
                                                </>
                                            ) : (
                                                <>
                                                    <Search className="w-4 h-4 mr-2" />
                                                    Vérifier disponibilité
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Timer Warning - Only show if we have reserved items */}
                            {reservedItems.length > 0 && !reservationUnlimited && (
                                <div className={`rounded-xl p-5 flex items-center justify-between ${
                                    isExpiringSoon 
                                        ? 'bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200' 
                                        : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
                                }`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                            isExpiringSoon ? 'bg-rose-100' : 'bg-amber-100'
                                        }`}>
                                            <Clock className={`w-6 h-6 ${isExpiringSoon ? 'text-rose-600 timer-warning' : 'text-amber-600'}`} />
                                        </div>
                                        <div>
                                            <p className={`font-semibold ${isExpiringSoon ? 'text-rose-800' : 'text-amber-800'}`}>
                                                Réservation expire dans
                                            </p>
                                            <p className={`text-3xl font-mono font-bold ${isExpiringSoon ? 'text-rose-600' : 'text-amber-600'}`}>
                                                {formattedTime}
                                            </p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        onClick={handleExtend}
                                        className={`rounded-lg font-semibold ${
                                            isExpiringSoon 
                                                ? 'border-rose-300 text-rose-700 hover:bg-rose-100' 
                                                : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                                        }`}
                                        data-testid="extend-reservation"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Prolonger +10min
                                    </Button>
                                </div>
                            )}
                            
                            {/* Negotiated Discount Banner */}
                            {hasNegotiatedDiscount && reservedItems.length > 0 && (
                                <div className="rounded-xl p-5 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 shadow-md">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-200">
                                            <Gift className="w-6 h-6 text-purple-700" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-bold text-purple-900 text-lg">
                                                Offre Négociée Active
                                            </p>
                                            <p className="text-purple-700">
                                                Une remise personnalisée de <strong>{totalDiscount.toFixed(2)} €</strong> a été appliquée à votre panier.
                                                Cette offre reste valable sans limite de temps.
                                            </p>
                                        </div>
                                        <Badge className="bg-purple-600 text-white text-sm px-3 py-1">
                                            <Tag className="w-4 h-4 mr-1" />
                                            -{totalDiscount.toFixed(0)} €
                                        </Badge>
                                    </div>
                                </div>
                            )}

                                {/* Products */}
                                {cart.items.map((product) => {
                                    const isReserved = product.availability_status === 'reserved';
                                    const isExpired = product.availability_status === 'expired';
                                    const isUnavailable = product.availability_status === 'unavailable' || product.availability_status === 'taken';
                                    
                                    return (
                                    <div 
                                        key={product.id} 
                                        className={`bg-white rounded-xl border p-5 shadow-sm transition-all ${
                                            isReserved 
                                                ? 'border-gray-200 hover:shadow-md' 
                                                : isExpired 
                                                    ? 'border-amber-300 bg-amber-50/30' 
                                                    : 'border-red-300 bg-red-50/30 opacity-75'
                                        }`}
                                        data-testid={`cart-item-${product.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-6">
                                            <div className="flex items-start gap-4 flex-1">
                                                {/* Availability indicator */}
                                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                                                    isReserved 
                                                        ? 'bg-emerald-50 border-emerald-200' 
                                                        : isExpired 
                                                            ? 'bg-amber-50 border-amber-200' 
                                                            : 'bg-red-50 border-red-200'
                                                }`}>
                                                    {isReserved ? (
                                                        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                                                    ) : isExpired ? (
                                                        <Clock className="w-7 h-7 text-amber-600" />
                                                    ) : (
                                                        <XCircle className="w-7 h-7 text-red-600" />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1">
                                                    {/* Availability status badge */}
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        {isReserved ? (
                                                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 rounded-full text-xs font-bold" data-testid={`status-${product.id}`}>
                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                Réservé
                                                            </Badge>
                                                        ) : isExpired ? (
                                                            <Badge className="bg-amber-100 text-amber-700 border-amber-300 rounded-full text-xs font-bold" data-testid={`status-${product.id}`}>
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                À vérifier
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-red-100 text-red-700 border-red-300 rounded-full text-xs font-bold" data-testid={`status-${product.id}`}>
                                                                <XCircle className="w-3 h-3 mr-1" />
                                                                Indisponible
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    <h3 
                                                        className="font-bold text-gray-900 text-lg flex items-center gap-2"
                                                        style={{ fontFamily: 'var(--font-heading)' }}
                                                    >
                                                        {product.brand} {product.model}
                                                        {product.storage_capacity && (
                                                            <span className="font-normal text-slate-600">{product.storage_capacity}Go</span>
                                                        )}
                                                        {product.color && (
                                                            <span className="font-normal text-slate-500">• {product.color}</span>
                                                        )}
                                                    </h3>
                                                    
                                                    {/* Product characteristics grid - like order detail */}
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-3 text-xs bg-slate-50 rounded-lg p-3">
                                                        {/* Grade */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Grade</span>
                                                            {product.grade ? (
                                                                <div className="flex items-center gap-1">
                                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                                                                        product.grade === 'A' ? 'bg-emerald-500' :
                                                                        product.grade === 'B' ? 'bg-blue-500' :
                                                                        product.grade === 'C' ? 'bg-amber-500' :
                                                                        'bg-red-500'
                                                                    }`}>
                                                                        {product.grade}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Version (EU, US, etc.) */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Version</span>
                                                            {(product.version || product.network || product.origin) ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Globe className="w-4 h-4 text-blue-500" />
                                                                    <span className="text-blue-600 font-medium">
                                                                        {product.version || product.network || product.origin}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* État */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">État</span>
                                                            {(product.aesthetic_condition || product.erp_etat) ? (
                                                                <div className="flex items-center gap-1">
                                                                    {(product.aesthetic_condition === 'Fonctionnel' || product.erp_etat?.toLowerCase() === 'fonctionnel') ? (
                                                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <Wrench className="w-4 h-4 text-amber-500" />
                                                                    )}
                                                                    <span className={`font-medium ${
                                                                        (product.aesthetic_condition === 'Fonctionnel' || product.erp_etat?.toLowerCase() === 'fonctionnel')
                                                                            ? 'text-emerald-600' : 'text-amber-600'
                                                                    }`}>
                                                                        {product.aesthetic_condition || product.erp_etat}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Lock */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Lock</span>
                                                            <div className="flex items-center gap-1">
                                                                {(!product.lock_status || product.lock_status === 'Débloqué' || product.lock_status === '-') ? (
                                                                    <>
                                                                        <Unlock className="w-4 h-4 text-emerald-500" />
                                                                        <span className="text-emerald-600 font-medium">Débloqué</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Lock className="w-4 h-4 text-red-500" />
                                                                        <span className="text-red-600 font-medium">{product.lock_status}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Cosmétique */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Cosm.</span>
                                                            {product.cosmetic_condition ? (
                                                                <div className="flex items-center gap-1">
                                                                    {product.cosmetic_condition === 'OK' ? (
                                                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                                                    )}
                                                                    <span className={`font-medium ${
                                                                        product.cosmetic_condition === 'OK' ? 'text-emerald-600' : 'text-amber-600'
                                                                    }`}>
                                                                        {product.cosmetic_condition}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Batterie */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Batt.</span>
                                                            {product.battery_health ? (() => {
                                                                // Parse battery health from string values like "80_85", "inf_80", "new", "sup_85", etc.
                                                                const bh = String(product.battery_health).toLowerCase();
                                                                let batteryLevel = 'unknown';
                                                                let displayText = product.battery_health;
                                                                
                                                                if (bh === 'new' || bh === 'neuve' || bh === 'sup_85' || bh.includes('>85') || bh.includes('sup')) {
                                                                    batteryLevel = 'high'; // >85%
                                                                    displayText = '>85%';
                                                                } else if (bh === '80_85' || bh.includes('80-85') || bh.includes('80_85')) {
                                                                    batteryLevel = 'medium'; // 80-85%
                                                                    displayText = '80-85%';
                                                                } else if (bh === 'inf_80' || bh.includes('<80') || bh.includes('inf') || bh === 'low') {
                                                                    batteryLevel = 'low'; // <80%
                                                                    displayText = '<80%';
                                                                } else if (bh === 'inconnu' || bh === 'unknown' || bh === '-') {
                                                                    batteryLevel = 'unknown';
                                                                    displayText = '?';
                                                                } else if (!isNaN(parseInt(bh))) {
                                                                    // Numeric value
                                                                    const numVal = parseInt(bh);
                                                                    if (numVal > 85) {
                                                                        batteryLevel = 'high';
                                                                        displayText = '>85%';
                                                                    } else if (numVal >= 80) {
                                                                        batteryLevel = 'medium';
                                                                        displayText = '80-85%';
                                                                    } else {
                                                                        batteryLevel = 'low';
                                                                        displayText = '<80%';
                                                                    }
                                                                }
                                                                
                                                                const colorClass = batteryLevel === 'high' ? 'text-emerald-500' :
                                                                                   batteryLevel === 'medium' ? 'text-amber-500' :
                                                                                   batteryLevel === 'low' ? 'text-red-500' : 'text-slate-400';
                                                                const textColorClass = batteryLevel === 'high' ? 'text-emerald-600' :
                                                                                       batteryLevel === 'medium' ? 'text-amber-600' :
                                                                                       batteryLevel === 'low' ? 'text-red-600' : 'text-slate-500';
                                                                
                                                                return (
                                                                    <div className="flex items-center gap-1">
                                                                        <Battery className={`w-4 h-4 ${colorClass}`} />
                                                                        <span className={`font-medium ${textColorClass}`}>
                                                                            {displayText}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Pièces */}
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-500 uppercase tracking-wide mb-1">Pièces</span>
                                                            {product.erp_pieces_incluses ? (
                                                                <div className="flex items-center gap-1">
                                                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    <span className="text-emerald-600 font-medium">Incluses</span>
                                                                </div>
                                                            ) : product.missing_parts ? (
                                                                <span className="text-amber-600 font-medium">{product.missing_parts}</span>
                                                            ) : (
                                                                <div className="flex items-center gap-1">
                                                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    <span className="text-emerald-600 font-medium">OK</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Pannes if any */}
                                                    {product.pannes && product.pannes.length > 0 && product.pannes.filter(p => p && p.toLowerCase() !== 'full ok').length > 0 && (
                                                        <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                                                            <div className="flex items-center gap-2 text-amber-700 text-xs">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                <span className="font-medium">Pannes:</span>
                                                                <span>{product.pannes.filter(p => p && p.toLowerCase() !== 'full ok').join(', ')}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* IMEI and TVA */}
                                                    <div className="flex items-center gap-3 mt-2">
                                                        <p className="text-xs font-mono text-slate-400">
                                                            IMEI: {product.imei}
                                                        </p>
                                                        <Badge 
                                                            variant="outline" 
                                                            className={`text-xs ${product.vat_regime === 'margin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                                                        >
                                                            {product.vat_regime === 'margin' ? 'TVA Marge' : 'TVA 20%'}
                                                        </Badge>
                                                    </div>
                                                    
                                                    {/* Availability reason for non-reserved items */}
                                                    {!isReserved && product.availability_reason && (
                                                        <p className={`text-xs mt-2 font-medium ${isExpired ? 'text-amber-600' : 'text-red-600'}`}>
                                                            {product.availability_reason}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="text-right flex flex-col items-end gap-3">
                                                <div>
                                                    {/* Check if this item has a negotiated discount */}
                                                    {discountItems[product.id] > 0 ? (
                                                        <>
                                                            <p className="text-sm font-mono text-slate-400 line-through">
                                                                {product.price.toFixed(0)} €
                                                            </p>
                                                            <p className="text-3xl font-bold font-mono text-purple-700">
                                                                {(product.price - discountItems[product.id]).toFixed(0)}
                                                                <span className="text-lg font-normal text-purple-500">€</span>
                                                            </p>
                                                            <Badge className="bg-purple-100 text-purple-700 text-xs mt-1">
                                                                -{discountItems[product.id].toFixed(0)} €
                                                            </Badge>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p className={`text-3xl font-bold font-mono ${isReserved ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                                                                {product.price.toFixed(0)}
                                                                <span className="text-lg font-normal text-slate-500">€</span>
                                                            </p>
                                                        </>
                                                    )}
                                                    <p className="text-xs text-gray-400">HT</p>
                                                </div>
                                                
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => handleRemove(product.id)}
                                                    className="rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                                    data-testid={`remove-${product.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                                    Retirer
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>

                            {/* Summary */}
                            <div className="lg:col-span-1">
                                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm sticky top-24">
                                    <h3 
                                        className="text-xl font-bold text-gray-900 mb-6"
                                        style={{ fontFamily: 'var(--font-heading)' }}
                                    >
                                        Récapitulatif
                                    </h3>

                                    <div className="space-y-4 mb-6">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Articles réservés ({reservedItems.length}/{cart.items.length})</span>
                                            <span className="font-mono font-semibold">{totalHT.toFixed(2)} €</span>
                                        </div>
                                        {expiredOrUnavailableItems.length > 0 && (
                                            <div className="flex justify-between text-amber-600 text-sm">
                                                <span>À vérifier</span>
                                                <span>{expiredOrUnavailableItems.length} article(s)</span>
                                            </div>
                                        )}
                                        
                                        {/* Show negotiated discount breakdown */}
                                        {hasNegotiatedDiscount && totalDiscount > 0 && (
                                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                                                <div className="flex justify-between text-purple-700 text-sm">
                                                    <span className="flex items-center gap-1">
                                                        <Tag className="w-3 h-3" />
                                                        Remise négociée
                                                    </span>
                                                    <span className="font-semibold">-{totalDiscount.toFixed(2)} €</span>
                                                </div>
                                                {Object.keys(discountItems).length > 0 && Object.values(discountItems).some(d => d > 0) && (
                                                    <p className="text-xs text-purple-600">
                                                        {Object.keys(discountItems).filter(k => discountItems[k] > 0).length} article(s) remisé(s)
                                                        {globalDiscount > 0 && ` + ${globalDiscount.toFixed(0)}€ globale`}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className="border-t border-gray-100 pt-4">
                                            <div className="flex justify-between items-end">
                                                <span className="text-lg font-semibold text-gray-900">Total HT</span>
                                                {hasNegotiatedDiscount && totalDiscount > 0 ? (
                                                    <div className="text-right">
                                                        <span className="text-sm font-mono text-slate-400 line-through block">
                                                            {totalHT.toFixed(0)} €
                                                        </span>
                                                        <span className="text-3xl font-bold text-purple-700 font-mono">
                                                            {totalAfterDiscount.toFixed(0)}
                                                            <span className="text-lg font-normal text-purple-500">€</span>
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-3xl font-bold text-gray-900 font-mono">
                                                        {totalHT.toFixed(0)}
                                                        <span className="text-lg font-normal text-gray-500">€</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {reservedItems.length === 0 ? (
                                        <div className="space-y-4">
                                            <Button 
                                                disabled
                                                className="w-full h-14 rounded-lg bg-gray-300 text-gray-500 font-bold text-lg cursor-not-allowed"
                                                data-testid="proceed-to-checkout-disabled"
                                            >
                                                Aucun article réservé
                                            </Button>
                                            <Button 
                                                onClick={handleVerifyAvailability}
                                                disabled={verifying}
                                                className="w-full h-12 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
                                                data-testid="verify-and-reserve-btn"
                                            >
                                                {verifying ? (
                                                    <>
                                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                        Vérification...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search className="w-4 h-4 mr-2" />
                                                        Vérifier et réserver
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button 
                                            onClick={async () => {
                                                // Refresh cart before checkout to verify products are still available
                                                const response = await fetchCart();
                                                // Navigate directly - fetchCart already updates cart state
                                                navigate('/checkout');
                                            }}
                                            className="w-full h-14 rounded-lg bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-colors"
                                            data-testid="proceed-to-checkout"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                                    Vérification...
                                                </>
                                            ) : (
                                                <>
                                                    Passer commande
                                                    <ArrowRight className="w-5 h-5 ml-2" />
                                                </>
                                            )}
                                        </Button>
                                    )}

                                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                        <div className="flex items-start gap-3">
                                            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-blue-700">
                                                <p className="font-semibold mb-1">Réservation garantie</p>
                                                <p className="text-blue-600">Vos articles réservés sont bloqués pendant 10 minutes pour éviter les doubles ventes.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </Layout>
    );
};

export default CartPage;
